import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { get } from '../utils/apiClient'

/**
 * Context centralise pour /me/entitlements.
 *
 * Source unique de verite pour:
 *   - orgType (alumni | diaspora | business | association | null)
 *   - memberTypeConfig (labels + badgeColors + available + showInVerifyProfile)
 *   - subscription (plan + status + dates)
 *   - canAccessPlatform
 *
 * Remplace les fetch dupliques dans MemberDetailsCard, AdminManagementTab,
 * VerifyProfile, etc. Un seul appel API par session authentifiee.
 */

const EntitlementsContext = createContext(null)

export const useEntitlements = () => {
  const context = useContext(EntitlementsContext)
  if (!context) {
    throw new Error('useEntitlements doit être utilisé dans un EntitlementsProvider')
  }
  return context
}

const PUBLIC_PATHS = [
  '/login',
  '/register',
  '/404',
  '/500',
  '/register-organization',
  '/email-validation-pending',
  '/validate-email',
  '/choose-plan',
  '/onboarding',
]

const isPublicPath = (pathname) => PUBLIC_PATHS.some((path) => pathname.startsWith(path))

export const EntitlementsProvider = ({ children }) => {
  const currentUser = useSelector((state) => state.user?.user)
  const userId = currentUser?.id || null
  const location = useLocation()

  const [orgType, setOrgType] = useState(null)
  const [memberType, setMemberType] = useState(null)
  const [memberTypeConfig, setMemberTypeConfig] = useState(null)
  const [subscription, setSubscription] = useState(null)
  const [canAccessPlatform, setCanAccessPlatform] = useState(true)
  const [hasCompleteEducation, setHasCompleteEducation] = useState(true) // default true to avoid flash
  // Signaux factuels de complétude du profil (photo / expérience / cursus).
  // null tant que non chargé → le nudge ne s'affiche pas (évite le flash).
  const [profileCompletion, setProfileCompletion] = useState(null)
  const [schoolId, setSchoolId] = useState(null)
  const [schoolName, setSchoolName] = useState(null)
  // Nombre de filières curated dans le référentiel — alimente le wizard
  // d'onboarding et le banner Dashboard "configuration incomplète".
  const [programsCount, setProgramsCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [initialized, setInitialized] = useState(false)
  const [error, setError] = useState(null)

  const resetEntitlements = useCallback(() => {
    setOrgType(null)
    setMemberType(null)
    setMemberTypeConfig(null)
    setSubscription(null)
    setCanAccessPlatform(true)
    setSchoolId(null)
    setSchoolName(null)
    setProgramsCount(0)
    setProfileCompletion(null)
  }, [])

  const fetchEntitlements = useCallback(async () => {
    if (!userId) {
      resetEntitlements()
      setLoading(false)
      setInitialized(true)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const data = await get('/me/entitlements')
      setOrgType(data.orgType || null)
      setMemberType(data.memberType || null)
      setMemberTypeConfig(data.memberTypeConfig || null)
      setSubscription(data.subscription || null)
      setCanAccessPlatform(data.canAccessPlatform !== false)
      setHasCompleteEducation(data.hasCompleteEducation !== false)
      setSchoolId(data.schoolId || null)
      setSchoolName(data.schoolName || null)
      setProgramsCount(typeof data.programsCount === 'number' ? data.programsCount : 0)
      setProfileCompletion(data.profileCompletion || null)
    } catch (err) {
      console.error('[EntitlementsContext] Erreur fetch:', err.message)
      resetEntitlements()
      setError(err.message || 'Erreur lors du chargement des entitlements')
    } finally {
      setLoading(false)
      setInitialized(true)
    }
  }, [userId, resetEntitlements])

  useEffect(() => {
    if (isPublicPath(location.pathname)) {
      setLoading(false)
      setInitialized(true)
      return
    }

    if (!userId) {
      resetEntitlements()
      setLoading(false)
      setInitialized(true)
      return
    }

    fetchEntitlements()
  }, [userId, location.pathname, fetchEntitlements, resetEntitlements])

  const value = {
    orgType,
    memberType,
    memberTypeConfig,
    subscription,
    canAccessPlatform,
    hasCompleteEducation,
    profileCompletion,
    schoolId,
    schoolName,
    programsCount,
    loading,
    initialized,
    error,
    refresh: fetchEntitlements,
  }

  return (
    <EntitlementsContext.Provider value={value}>{children}</EntitlementsContext.Provider>
  )
}

export default EntitlementsProvider
